import { Check } from 'lucide-react'

import { cn } from '@ui/lib/utils'
import { Button } from './ui/button'

export interface FilterBarChartItem {
  key: string
  label: string
  value: number
  baselineValue?: number
  description?: string | null
}

export interface FilterBarChartProps {
  label: string
  items: FilterBarChartItem[]
  maximum: number
  selectedKeys?: string[]
  disabled?: boolean
  itemLabel?: string
  multiSelect?: boolean
  onSelect: (key: string, additive: boolean) => void
}

/** A distribution chart whose keyboard-accessible bars act as toggle filters. */
export function FilterBarChart({
  label,
  items,
  maximum,
  selectedKeys = [],
  disabled = false,
  itemLabel = 'Rückmeldungen',
  multiSelect = true,
  onSelect,
}: FilterBarChartProps) {
  const scale = Number.isFinite(maximum) && maximum > 0 ? maximum : 1

  return (
    <ul aria-label={label}>
      {items.map((item) => {
        const selected = selectedKeys.includes(item.key)
        const value = Number.isFinite(item.value) ? Math.max(0, item.value) : 0
        const baselineValue = Number.isFinite(item.baselineValue)
          ? Math.max(0, item.baselineValue ?? 0)
          : value
        const description = item.description
          ? `${item.label} · ${item.description}`
          : item.label

        return (
          <li key={item.key}>
            <Button
              type="button"
              variant="ghost"
              className={cn(
                'grid h-6 min-h-0 w-full grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto] gap-2 rounded-sm px-1 py-0 text-left text-xs font-normal',
                selected && 'bg-accent font-semibold text-accent-foreground',
              )}
              aria-pressed={selected}
              aria-label={`${description}: ${value.toLocaleString('de-DE')} ${itemLabel}. ${selected ? 'Ausgewählt. ' : ''}Klicken zum Filtern${multiSelect ? ', Strg + Klick für Mehrfachauswahl' : ''}.`}
              title={description}
              disabled={disabled}
              onClick={(event) =>
                onSelect(item.key, multiSelect && (event.ctrlKey || event.metaKey))
              }
            >
              <span className="flex min-w-0 items-center gap-1">
                {selected && <Check className="size-3 shrink-0" aria-hidden="true" />}
                <span className="truncate">{item.label}</span>
              </span>
              <span
                className="relative h-2 overflow-hidden rounded-sm"
                aria-hidden="true"
              >
                <span
                  className="absolute inset-y-0 left-0 rounded-sm bg-primary/20 transition-[width] duration-150 motion-reduce:transition-none"
                  style={{ width: `${Math.min(100, (baselineValue / scale) * 100)}%` }}
                />
                <span
                  className="absolute inset-y-0 left-0 rounded-sm bg-primary transition-[width] duration-150 motion-reduce:transition-none"
                  style={{ width: `${Math.min(100, (value / scale) * 100)}%` }}
                />
              </span>
              <span className="min-w-6 text-right tabular-nums" aria-hidden="true">
                {value > 0 || item.baselineValue === undefined
                  ? value.toLocaleString('de-DE')
                  : ''}
              </span>
            </Button>
          </li>
        )
      })}
    </ul>
  )
}
